/**
 * Types for AI Content Generation
 * Based on backend integrations/serializers.py and content/serializers.py
 */

// Status values matching backend (integrations/models.py)
export type JobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
export type StepStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

/**
 * JobStep from JobStepSerializer (integrations/serializers.py)
 */
export interface JobStep {
  id: string;
  job: string;
  step_order: number;
  name: string;
  step_type: string;
  integration_config: string | null;
  integration_config_name: string | null;
  integration_version_used: number | null;
  input_params: Record<string, unknown>;
  status: StepStatus;
  output: Record<string, unknown> | null;
  error: string;
  retry_count: number;
  max_retries: number;
  duration_seconds: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

/**
 * Progress from Job.progress property (integrations/models.py)
 */
export interface JobProgress {
  total_steps: number;
  completed_steps: number;
  failed_steps: number;
  percentage: number;
  /**
   * EAA-2027: live generation progress, merged by newer Hubs into this object next
   * to the step counts (`percentage` stays step-based). Null when unknown (pending
   * job, old Agent) and absent on older backends — the UI then shows the plain
   * "Processing" pill exactly as before. On a bulk parent, `percent` is the mean of
   * the children and `eta_s` the max over the running ones.
   */
  percent?: number | null;
  phase?: GenerationPhase | null;
  /** Seconds the job still needs, as of the response (0 once completed). */
  eta_s?: number | null;
  /** Seconds the whole job is expected to take (shown before real progress starts). */
  estimate_s?: number | null;
}

/** EAA-2027: pipeline phase reported in `progress.phase`. */
export type GenerationPhase =
  | 'queued'
  | 'grounding'
  | 'planning'
  | 'structure'
  | 'drawing'
  | 'writing'
  | 'checking'
  | 'done';

/**
 * JobDetail from JobDetailSerializer (integrations/serializers.py)
 * Response from GET /integrations/jobs/{id}
 */
export interface JobDetail {
  id: string;
  tenant: string;
  tenant_slug: string;
  name: string;
  description: string;
  status: JobStatus;
  metadata: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string;
  webhook_url: string;
  webhook_events: string[];
  progress: JobProgress;
  duration_seconds: number;
  steps: JobStep[];
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
}

/**
 * Job payload without steps (GET /integrations/jobs/{id}?minimal=true or bulk children)
 */
export interface JobSummary {
  id: string;
  tenant: string;
  tenant_slug: string;
  name: string;
  description: string;
  status: JobStatus;
  metadata: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string;
  progress: JobProgress;
  duration_seconds: number;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
}

/** GET /content/bulk-generate/{parentJobId} */
export interface BulkJobDetail {
  id: string;
  tenant: string;
  tenant_slug: string;
  name: string;
  description: string;
  status: JobStatus;
  metadata: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string;
  progress: JobProgress;
  duration_seconds: number;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  children: JobSummary[];
}

/**
 * Request for POST /content/generate
 * Based on content/serializers.py ContentGenerateSerializer
 * 
 * Note: content_type_id is now auto-resolved by backend from question_type slug
 */
export interface GenerateContentRequest {
  /** AI prompt describing the content to generate (required, max 5000 chars) */
  prompt: string;
  /** AI content type slug (e.g., 'qt-dropdown', 'qt-matching') - required */
  question_type: string;
  /** Tenant ID for multi-tenant context */
  tenant_id?: string;
  /** Content format (default: 'h5p') */
  content_format?: string;
  /** Optional title for the generated content (max 255 chars) */
  title?: string;
  /** Optional URL to receive webhook notifications on job events */
  webhook_url?: string;
  /** Optional additional metadata for the job */
  metadata?: Record<string, unknown>;
  /** Optional payload for AI Agent gen-question (e.g. media, num_blanks, ui_hint) */
  option?: Record<string, unknown>;
  /** Optional module to auto-link after generation completes */
  module_id?: string;
  /** EAA-1739: initial Content.status to save as (e.g. 2=draft, 3=published) */
  status?: number;
}

/** Multipart POST /content/generate-from-document (worksheet .docx/.pdf + prompt). */
export interface GenerateContentFromDocumentRequest extends GenerateContentRequest {
  file: File;
}

/**
 * Response from POST /content/generate
 * Based on content/serializers.py JobResponseSerializer
 */
export interface GenerateContentResponse {
  job_id: string;
  status: string;
}

/** One row in POST /content/bulk-generate/ */
export interface BulkContentGenerateItem {
  prompt: string;
  question_type: string;
  content_format?: string;
  title?: string;
  webhook_url?: string;
  metadata?: Record<string, unknown>;
  option?: Record<string, unknown>;
  module_id?: string;
  /** easy | medium | hard — API defaults to easy when omitted */
  difficulty?: 'easy' | 'medium' | 'hard';
  /**
   * EAA-2063: the textbook activity (``Activity.ref`` from
   * POST /program/programs/textbook-activities/) this question is generated from.
   * Omitted for a lesson with no exercises in the book (lesson-content fallback).
   */
  source_activity_ref?: string;
  /**
   * EAA-2063: textbook generation mode — ``convert`` keeps the book's exact items and
   * answers, ``paraphrase`` writes new content on the same target language. When set,
   * the API accepts an empty ``prompt``.
   */
  mode?: 'convert' | 'paraphrase';
  /** EAA-2063: whether the question may generate audio; omitted when the user had no choice. */
  generate_audio?: boolean;
  /** EAA-2063: whether the question may generate pictures; omitted when the user had no choice. */
  generate_image?: boolean;
}

export interface BulkContentGenerateRequest {
  items: BulkContentGenerateItem[];
  tenant_id?: string;
}

/**
 * EAA-2063: the part of a generated item's source provenance the review list reads
 * (the full shape lives with the lesson tree's content items).
 */
export interface BulkGeneratedContentProvenance {
  pages?: { start: number; end: number } | null;
  page_numbering?: 'printed' | 'pdf';
  [key: string]: unknown;
}

/** EAA-2063: one item a bulk generation saved — GET /content/bulk-generate/{id}/contents/ */
export interface BulkGeneratedContent {
  id: string;
  title: string;
  ai_content_type_slug: string;
  difficulty: 'easy' | 'medium' | 'hard' | null;
  status: string | number;
  module_id: string | null;
  module_title: string | null;
  source_provenance: BulkGeneratedContentProvenance | null;
  created_at: string;
}

export interface BulkGeneratedContentsResponse {
  job_id: string;
  /** False while children are still running; the list then grows. */
  done: boolean;
  contents: BulkGeneratedContent[];
}

/** EAA-2063: POST /content/bulk-generate/{id}/discard/ */
export interface BulkDiscardContentsRequest {
  content_ids: string[];
}

export interface BulkDiscardContentsResponse {
  deleted: string[];
  skipped: { id: string; reason: string }[];
}

export interface BulkContentGenerateJobItem {
  job_id: string;
  status: string;
  module_id?: string | null;
}

export interface BulkContentGenerateResponse {
  parent_job_id: string;
  jobs: BulkContentGenerateJobItem[];
}

/**
 * EAA-2027: response of POST /content/bulk-generate/estimate/ — how long the given
 * items should take, before they are submitted. `estimate_s` is null when the Hub
 * has no basis for a number (e.g. no timing history yet); `concurrency` is null when
 * it cannot tell how many jobs run at once.
 */
export interface BulkGenerateEstimateResponse {
  estimate_s: number | null;
  jobs: number;
  concurrency: number | null;
}

/**
 * Request for POST /content/{id}/regenerate
 * Based on content/serializers.py ContentRegenerateSerializer
 */
export interface RegenerateContentRequest {
  /** AI prompt describing the content to generate (required, max 5000 chars) */
  prompt: string;
  /** AI content type slug (e.g., 'qt-dropdown', 'qt-matching') - required */
  question_type: string;
  /** Tenant ID for multi-tenant context */
  tenant_id?: string;
  /** Content format (default: 'h5p') */
  content_format?: string;
  /** Optional URL to receive webhook notifications on job events */
  webhook_url?: string;
  /** Optional additional metadata for the job */
  metadata?: Record<string, unknown>;
  /** Optional payload for AI Agent gen-question (e.g. media, num_blanks, ui_hint) */
  option?: Record<string, unknown>;
  /**
   * EAA-2029: how a chat message applies to the saved set. `patch` edits only the
   * targeted sub-questions (0-based `target_items`; omitted = the Agent reads them from
   * the prompt); `regenerate` rebuilds the whole set but keeps its question count.
   * Omitted = legacy whole-content regeneration.
   */
  edit?: {
    mode: 'patch' | 'regenerate';
    target_items?: number[] | null;
  };
}

/**
 * Helper function to check if job is in terminal state
 */
export function isJobTerminal(status: JobStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

/**
 * Helper function to check if job is still running
 */
export function isJobRunning(status: JobStatus): boolean {
  return status === 'pending' || status === 'running';
}
